import { QueryKeys } from "@Constants";
import { IAssessmentDetails } from "@Models/IAssessmentDetails";
import { IFeedbackInstanceDetails } from "@Models/IFeedbackInstanceDetails";
import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { ITaskSubmissionDetails } from "@Models/ITaskDetails";
import { IUserDetails } from "@Models/IUserDetails";
import AssessmentService from "@Services/AssessmentService";
import FeedbackService from "@Services/FeedbackService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { IAssessmentMetadataRequest } from "./types";
import SessionManager from "@Core/session";

export const useCreateTemplate = () => {
    const feedbackService = new FeedbackService();
    const queryClient = useQueryClient();

    return useMutation(
        ({ groupId, model }: { groupId: IFeedbackTemplateDetails["groupId"]; model: IFeedbackTemplateDetails }) =>
            feedbackService.createTemplate(groupId, model),
        {
            onSuccess: (res) => {
                if (res.value.id) {
                    queryClient.invalidateQueries([QueryKeys.Templates]);
                }
            },
        }
    );
};

export const useCreateAssessmentMetadata = (userId = SessionManager.getServiceUser().userId) => {
    const assessmentService = new AssessmentService();

    return useMutation(
        ({ req: { assessmentId, assessmentInstanceId } }: { req: IAssessmentMetadataRequest }) =>
            assessmentService.createMetadata(userId, assessmentId, assessmentInstanceId),
        {
            onSuccess: (res) => {
            if (res?.value?.id) {
                // nothing yet
            }
            },
        }
    );
};

export const useDeleteTemplate = () => {
  const feedbackService = new FeedbackService();
  const queryClient = useQueryClient();

  return useMutation(
    ({
      groupId,
      templateId,
    }: {
      groupId: IFeedbackTemplateDetails["groupId"];
      templateId: IFeedbackTemplateDetails["templateId"];
    }) => feedbackService.deleteTemplate(groupId, templateId),
    {
      onSuccess: (res) => {
        if (res.value) {
          queryClient.invalidateQueries([QueryKeys.Templates]);
        }
      },
    }
  );
};

export const useUpdateTemplate = () => {
  const feedbackService = new FeedbackService();
  const queryClient = useQueryClient();

  return useMutation(
    ({ groupId, model }: { groupId: IFeedbackTemplateDetails["groupId"]; model: IFeedbackTemplateDetails }) =>
      feedbackService.updateTemplate(groupId, model),
    {
      onSuccess: (res) => {
        if (res.value) {
          queryClient.invalidateQueries([QueryKeys.Templates]);
        }
      },
    }
  );
};

export const useCreateAssessment = (userId: IUserDetails["userId"]) => {
  const assessmentService = new AssessmentService();
  const queryClient = useQueryClient();

  return useMutation(({ model }: { model: IAssessmentDetails }) => assessmentService.create(userId, model), {
    onSuccess: (res) => {
      if (res.value.id) {
        notification.success({ message: "Assessment created." });
        queryClient.invalidateQueries([QueryKeys.AssessmentList, userId]).then();
        queryClient.invalidateQueries([QueryKeys.AssessmentUserInstanceList]).then();
      }
    },
  });
};

export const useCompleteAssessmentInstance = (assessmentId: IAssessmentDetails["assessmentId"]) => {
  const assessmentService = new AssessmentService();
  const queryClient = useQueryClient();

  return useMutation(
    ({ model }: { model: IFeedbackInstanceDetails }) => assessmentService.completeInstance(assessmentId, model),
    {
      onSuccess: (res) => {
        if (res.value) {
          notification.success({ message: "Assessment completed." });
          queryClient.invalidateQueries([QueryKeys.AssessmentInstanceList, assessmentId]).then();
        }
      },
    }
  );
};

export const useSubmitTasks = (userId: IUserDetails["userId"], assessmentId: IAssessmentDetails["assessmentId"]) => {
  const assessmentService = new AssessmentService();
  const queryClient = useQueryClient();

  return useMutation(
      ({ model }: { model: ITaskSubmissionDetails[] }) => assessmentService.submit(userId, assessmentId, model),
      {
          onSuccess: async (res) => {
              if (res.hasErrors) {
                  return notification.error({ message: "Error submitting tasks." });
              }
              if (res.value) {
                  notification.success({ message: "Tasks submitted successfully." });
                  await queryClient.invalidateQueries([QueryKeys.AssessmentUserInstanceList]);
              }
          },
      }
  );
};

export const useUpdateAssessment = (assessmentId: IAssessmentDetails["assessmentId"]) => {
  const assessmentService = new AssessmentService();
  const queryClient = useQueryClient();

  return useMutation(({ model }: { model: IAssessmentDetails }) => assessmentService.update(assessmentId, model), {
    onSuccess: (res) => {
      if (!res.errors.length && res.value) {
        notification.success({ message: "Assessment updated." });
        queryClient.invalidateQueries([QueryKeys.AssessmentList]).then();
      } else {
        console.error("res.errors:", res.errors);
        notification.error({ message: "Error updating assessment." });
      }
    },
  });
};
